import { Button, DatePicker, Select, Space } from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import RefreshIcon from "../../assets/icons/RefreshIcon";
import Map from "../../components/NP/Dashboard/Map";
import SiteLocator from "../../components/NP/Dashboard/SiteLocator";
import TopBarLog from "../../components/NP/Dashboard/TopBarLog";
import IconButton from "../../components/common/IconButton";
import Loader from "../../components/common/Loader";
import { ArrayOption, selectSearchOption } from "../../helpers/siteConfig";
import AdminLayout from "../../layouts/AdminLayout";
import {
  getActiveAndInactiveMapAsync,
  getActiveAndInactiveTowerAsync,
  getDashboardLogAsync,
} from "../../store/features/NP/dashboard/dashboardAPI";
import {
  getAllAreaAsync,
  getAllRegionAsync,
  getAllTerritoryAsync,
} from "../../store/features/NP/geoInfo/geoAPI";
import { useAppDispatch, useAppSelector } from "../../store/store";
import { DashboardParams, RegionalParams } from "../../types/redux";
import { formatDate, previousDate } from "../../utils/time";
import { SupplierGetAllParams } from "../../types/redux/NP/supplier";
import useTable from "../../hooks/useTable";
import useDebounce from "../../hooks/useDebounce";
import { getAllSupplierAsync } from "../../store/features/NP/supplier/supplierAPI";

export default function Dashboard() {
  const dispatch = useAppDispatch();
  const { login } = useAppSelector((state) => state.login);
  const { loading } = useAppSelector((state) => state.dashboard);
  const [refresh, setRefresh] = useState(false);
  const { regions, areas, territorys } = useAppSelector((state) => state.npGeo);
  const { allSupplier } = useAppSelector((state) => state.supplier);
  const [regionId, setRegionId] = useState<string | undefined>(undefined);
  const [areaId, setAreaId] = useState(undefined);
  const [territoryId, setTerritoryId] = useState(undefined);
  const [supplierId, setSupplierId] = useState(undefined);
  const { page, limit } = useTable();
  const { keyword } = useDebounce();
  // const handleDateChange: DatePickerProps["onChange"] = (date, dateString) => {
  //   console.log(date, dateString);
  // };

  const [today, setToday] = useState(true);
  const [date, setDate] = useState({
    from: "",
    to: "",
  });

  const removeRefresh = () => {
    setRefresh(false);
  };

  const handleRefresh = () => {
    const params = {} as RegionalParams;

    if (
      login?.role === "region" ||
      login?.role === "finance" ||
      login?.role === "vendor"
    ) {
      params.region = login?.region;
    } else {
      setRegionId(undefined);
    }

    setAreaId(undefined);
    setTerritoryId(undefined);

    dispatch(getActiveAndInactiveTowerAsync({ params }));
    dispatch(getActiveAndInactiveMapAsync({ params }));
    dispatch(
      getDashboardLogAsync({
        params: {
          today: previousDate(formatDate(new Date(), "YYYY-MM-DD")),
          ...(login?.role === "region" ||
          login?.role === "finance" ||
          login?.role === "vendor"
            ? { region: login?.region }
            : {}),
        },
      })
    );
    setRefresh(true);
    setDate({
      from: "",
      to: "",
    });
    setToday(true);
  };

  const supplierData = useCallback(() => {
    const params: SupplierGetAllParams = {
      // limit: limit,
      // skip: limit * (page - 1),
    };

    const searchParams: SupplierGetAllParams = {
      supplierName: keyword,
    };

    if (login?.role === "region" || login?.role === "finance") {
      params.region = login?.region;
      searchParams.region = login?.region;
    }

    dispatch(
      getAllSupplierAsync({
        params: keyword ? searchParams : params,
      })
    );
  }, [dispatch, keyword, login?.region, login?.role]);

  useEffect(() => {
    supplierData();
  }, [supplierData]);

  useEffect(() => {
    let params: DashboardParams = {};

    if (today) {
      params.today = previousDate(formatDate(new Date(), "YYYY-MM-DD"));
    } else {
      params.startDate = date.from ? previousDate(date.from) : "";
      params.endDate = date.to ? previousDate(date.to) : "";
    }

    if (
      login?.role === "region" ||
      login?.role === "finance" ||
      login?.role === "vendor"
    ) {
      params.region = login?.region;
    } else {
      params.region = regionId;
    }

    params.area = areaId;
    params.teritory = territoryId;
    params.suppler = supplierId;

    dispatch(getDashboardLogAsync({ params }));
  }, [
    areaId,
    date.from,
    date.to,
    dispatch,
    login?.region,
    login?.role,
    regionId,
    territoryId,
    supplierId,
    today,
  ]);

  useEffect(() => {
    const params = {} as RegionalParams;

    if (
      login?.role === "region" ||
      login?.role === "finance" ||
      login?.role === "vendor"
    ) {
      params.region = login?.region;
    }

    dispatch(getActiveAndInactiveTowerAsync({ params }));
    dispatch(getActiveAndInactiveMapAsync({ params }));
  }, [dispatch, login?.region, login?.role]);

  useEffect(() => {
    dispatch(getAllRegionAsync({}));
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      getAllAreaAsync({
        params: {
          region: regionId,
        },
      })
    );
  }, [dispatch, regionId]);

  useEffect(() => {
    if (areaId)
      dispatch(
        getAllTerritoryAsync({
          params: {
            area: areaId,
          },
        })
      );
  }, [dispatch, areaId]);

  useEffect(() => {
    if (
      login?.role === "region" ||
      login?.role === "finance" ||
      login?.role === "vendor"
    ) {
      setRegionId(login?.region);
    }
  }, [login?.region, login?.role]);

  return (
    <AdminLayout
      title="Dashboard"
      headerTitle="Dashboard"
      className="dashboard"
      headerChildren={
        <Space size="middle" className="flex-wrap">
          <Select
            placeholder="Select cluster"
            size="large"
            allowClear
            showSearch
            filterOption={selectSearchOption}
            options={ArrayOption(regions, "regionName", "_id", true)}
            onChange={(val) => {
              setRegionId(val);
              setAreaId(undefined);
              setTerritoryId(undefined);
            }}
            value={regionId}
            className="min-w-[120px]"
            disabled={
              login?.role === "region" ||
              login?.role === "finance" ||
              login?.role === "vendor"
            }
          />
          <Select
            placeholder="Select zone"
            size="large"
            allowClear
            showSearch
            filterOption={selectSearchOption}
            options={ArrayOption(areas, "areaName", "_id", true)}
            onChange={(val) => {
              setAreaId(val);
              setTerritoryId(undefined);
            }}
            value={areaId}
            disabled={!regionId}
            className="min-w-[120px]"
          />
          <Select
            placeholder="Maintenance point"
            size="large"
            allowClear
            showSearch
            filterOption={selectSearchOption}
            options={ArrayOption(territorys, "teritoryName", "_id", true)}
            onChange={(val) => {
              setTerritoryId(val);
            }}
            value={territoryId}
            disabled={!areaId}
            className="min-w-[120px]"
            optionFilterProp="children"
          />

          <Select
            placeholder="Vendor"
            size="large"
            allowClear
            showSearch
            filterOption={selectSearchOption}
            options={ArrayOption(allSupplier, "supplierName", "_id")}
            onChange={(val) => {
              setSupplierId(val);
            }}
            value={supplierId}
            // disabled={!areaId}
            className="min-w-[120px]"
            optionFilterProp="children"
          />

          <Button
            className={today ? "" : "!border-accent"}
            type={today ? "primary" : "default"}
            onClick={() => {
              setDate({
                from: "",
                to: "",
              });
              setToday(true);
            }}
          >
            Today
          </Button>
          <DatePicker.RangePicker
            format="YYYY-MM-DD"
            // size={innerWidth > 768 ? "large" : "middle"}
            // style={{ width: innerWidth > 768 ? "270px" : "240px" }}
            className="date-range"
            // popupClassName="one-month-date-range"
            value={[
              date?.from ? dayjs(date?.from, "YYYY-MM-DD") : null,
              date?.to ? dayjs(date?.to, "YYYY-MM-DD") : null,
            ]}
            onChange={(val) => {
              setDate({
                from: val?.[0]?.format("YYYY-MM-DD") || "",
                to: val?.[1]?.format("YYYY-MM-DD") || "",
              });

              setToday(val ? false : true);
            }}
          />

          <IconButton
            onClick={handleRefresh}
            type="primary"
            nextIcon={<RefreshIcon />}
          >
            Refresh
          </IconButton>
          {/* <DatePicker onChange={handleDateChange} /> */}
        </Space>
      }
    >
      {loading ? (
        <div className="h-28">
          <Loader />
        </div>
      ) : null}
      <section>
        {/* className="grid grid-cols-2 lg:grid-cols-3 gap-5" */}
        <div>
          {/* className="lg:col-span-2" */}
          {/* <div className="grid md:grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2">
              <TopBar />
              
            </div>
          </div> */}
          <TopBarLog today={today} date={date} />
          <Map />

          {login?.role !== "finance" ? (
            <SiteLocator
              refresh={refresh}
              removeRefresh={removeRefresh}
              keyword={keyword}
              siteCode={""}
              filter={{ regionId, areaId, territoryId, supplierId, date }}
            />
          ) : null}
        </div>

        {/* <RequestList /> */}
      </section>
    </AdminLayout>
  );
}
